import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AudioUnlockService {
  private unlocked = false;
  private resolver?: () => void;
  private unlockPromise: Promise<void>;
  readonly unlocked$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.unlockPromise = new Promise<void>((resolve) => {
      this.resolver = resolve;
    });
  }

  // Call this from any user gesture (click/touch/keydown) once
  unlock(): void {
    if (this.unlocked) return;
    this.unlocked = true;
    this.unlocked$.next(true);

    // Best-effort: resume WebAudio if available
    try {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        if (ctx.state === 'suspended') {
          ctx.resume().catch(() => {});
        }
        // Play a tiny silent buffer (unlocks WebAudio on Safari)
        const buf = ctx.createBuffer(1, 1, 22050);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(ctx.destination);
        src.start(0);
        setTimeout(() => {
          try { ctx.close(); } catch {}
        }, 250);
      }
    } catch {}

    // Resolve waiters
    try {
      this.resolver?.();
    } catch {}
  }

  // Await until first user gesture happened (optional timeout)
  async waitUntilUnlocked(timeoutMs = 10000): Promise<void> {
    if (this.unlocked) return;
    return new Promise<void>((resolve) => {
      let to: any;
      const done = () => {
        clearTimeout(to);
        resolve();
      };
      this.unlockPromise.then(done).catch(done);
      to = setTimeout(done, timeoutMs);
    });
  }
}
